import React from 'react';

import { cn } from '@crossnokaye/ux-common';

import { InputValueChange } from '@/ux-common/components/input/input';
import { Stack } from '@/ux-common/components/stack/stack';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@/ux-common/components/tooltip/tooltip';

interface SequenceBoxProps {
  // Children to display inside the box. Typically the stage value. Must stay
  // non-interactive: the trigger cancels the click/pointerdown defaults to keep
  // the tooltip open through activation, and that same preventDefault would
  // cancel a child link's navigation or a child control's default action.
  children?: React.ReactNode;
  // Classname to apply to the box. Typically helps with background color.
  className?: string;
  // Tooltip text to display when disabled (e.g., permission message).
  disabledTooltipText?: string;
  // Whether interactions are disabled (e.g., due to permissions or read-only mode).
  isDisabled?: boolean;
  // Whether this cell has been modified from its original value.
  isDirty?: boolean;
  // Whether this cell is unavailable (disables click and shows not-allowed cursor).
  isUnavailable?: boolean;
  // Function to call when the box is clicked.
  onClick?: () => void;
  // The original value of the box. Typically the original stage value.
  originalValue?: string;
  // Style to apply to the box. Typically helps with background color.
  style?: React.CSSProperties;
  // Tooltip content to display when the box is hovered.
  tooltipContent: string;
}

/**
 * A sequencer stage cell that is simultaneously the control being clicked and a
 * tooltip anchor (stage label, dirty preview, permission message).
 *
 * Touch devices never see the tooltip: Radix opens tooltips only from pointer
 * hover or keyboard focus, and a tap provides neither. Taps still fire onClick,
 * so values can be changed on touch — the previous-value preview and the
 * disabled explanation are hover-only, as an accepted trade-off: the sequencer
 * is edited from a desktop, and the dirty state itself stays visible on every
 * device through the warning outline and corner triangle.
 */
export const SequenceBox: React.FC<SequenceBoxProps> = ({
  children,
  className,
  disabledTooltipText,
  isDisabled,
  isDirty,
  isUnavailable,
  onClick,
  originalValue,
  style,
  tooltipContent,
}) => {
  return (
    <Tooltip>
      <TooltipTrigger
        asChild
        // Radix closes a tooltip on trigger activation, and the sequencer needs it
        // to survive the click — the box IS the control being clicked. Preventing
        // default is Radix's supported stay-open hatch: composeEventHandlers skips
        // the internal close when the caller's handler prevented default, and a div
        // has no default action to lose. Everything else — when to open, when to
        // close on leave — stays with the provider, so timing has one owner.
        onClick={(event) => event.preventDefault()}
        onPointerDown={(event) => event.preventDefault()}
      >
        <div
          className={cn(
            'flex items-center justify-center h-full relative isolate',
            !isUnavailable &&
              'hover:outline hover:outline-2 hover:outline-ckds-input-border-color-focus',
            onClick && !isDisabled ? 'hover:cursor-pointer' : '',
            isUnavailable && 'hover:cursor-not-allowed',
            isDirty && 'outline outline-2 outline-warning'
          )}
          data-component="SequenceBox"
          onClick={isDisabled ? undefined : onClick}
        >
          {/* Background layer with opacity */}
          <div className={cn('absolute inset-0', className)} style={style} />
          {/* Top-right corner triangle indicator for dirty state */}
          {isDirty && (
            <div
              className="absolute top-0 right-0 z-[2] w-0 h-0"
              style={{
                borderTop: '16px solid hsl(var(--ckds-warning))',
                borderLeft: '16px solid transparent',
              }}
            />
          )}
          {/* Content layer without opacity */}
          <div className="relative z-[1] flex items-center justify-center">{children}</div>
        </div>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent
          align={'center'}
          side="bottom"
          className={cn('m-0 gap-0.5')}
          // The other half of surviving the click: the content sits on a
          // DismissableLayer that treats a pointerdown on the TRIGGER as an
          // outside press. Hover is the only thing keeping this tooltip up, so
          // pointer-leave still closes it everywhere else.
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <Stack className="stack-thin">
            <div className="flex items-center gap-1">
              <span className="font-bold">{tooltipContent}</span>
              {isDirty && originalValue && (
                /* The registry slot is self-contained — `.input-value-change` reads
                     theme custom properties and has no ancestor selector — so it works
                     here, outside an `Input` group. It already mutes the old value, so
                     the `text-muted-foreground` this used to pass is redundant, and it
                     adds an sr-only "Previous value" the hand-rolled label lacked. */
                <InputValueChange oldValue={originalValue.toString()} />
              )}
            </div>
            {isDisabled && disabledTooltipText && <span>{disabledTooltipText}</span>}
          </Stack>
          <TooltipArrow />
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
};
